import { SampleRequestFormState, SampleRequestFormAction } from './useSampleRequestForm.types';

export const initialState: SampleRequestFormState = {
  currentStep: 0,
  sampleFormData: {},
  selectedImage: null,
  imagePreview: null,
  isImageDragging: false,
  isSubmitting: false,
  isSavingDraft: false,
  savedDraftId: null,
  savedDraftInfo: null,
  submittedRequest: null,
  showSuccessScreen: false,
  showDraftSuccessScreen: false,
  copiedField: null,
  inputMode: 'select',
  benchmarkImageUrl: '',
  benchmarkImagePreview: '',
  benchmarkStorageKey: '',
  isUploadingBenchmark: false,
  isAnalyzingBenchmark: false,
  benchmarkAnalysisResult: null,
  analysisError: null,
  emailVerificationSent: false,
  verificationCode: '',
  isVerifyingEmail: false,
  isSendingVerification: false,
  emailVerified: false,
  showPasswordLogin: false,
  loginPassword: '',
  isPasswordLoggingIn: false,
  selectedBrandId: null,
};

export function sampleRequestFormReducer(
  state: SampleRequestFormState,
  action: SampleRequestFormAction
): SampleRequestFormState {
  switch (action.type) {
    // === Initialization ===
    case 'RESTORE_FROM_SESSION': {
      const p = action.payload;
      return {
        ...state,
        sampleFormData: p.formData || state.sampleFormData,
        currentStep: p.currentStep ?? state.currentStep,
        inputMode: p.inputMode || state.inputMode,
        benchmarkImageUrl: p.benchmarkImageUrl || state.benchmarkImageUrl,
        benchmarkImagePreview: p.benchmarkImagePreview || state.benchmarkImagePreview,
        benchmarkAnalysisResult: p.benchmarkAnalysisResult ?? state.benchmarkAnalysisResult,
        savedDraftId: p.savedDraftId ?? state.savedDraftId,
        imagePreview: p.imagePreview ?? state.imagePreview,
        emailVerified: p.isAuthenticated ? true : state.emailVerified,
      };
    }

    case 'INITIALIZE_DEFAULT':
      return {
        ...initialState,
        sampleFormData: action.payload.formData,
        currentStep: action.payload.firstEmptyStep,
        emailVerified: action.payload.emailVerified,
      };

    case 'RESET_FORM':
      return { ...initialState };

    // === Form Navigation ===
    case 'SET_STEP':
      return { ...state, currentStep: action.payload };

    case 'NEXT_STEP':
      return { ...state, currentStep: state.currentStep + 1 };

    case 'PREV_STEP':
      return { ...state, currentStep: Math.max(0, state.currentStep - 1) };

    case 'SKIP_STEP': {
      const newData = { ...state.sampleFormData };
      delete newData[action.payload.currentField];
      return {
        ...state,
        sampleFormData: newData,
        currentStep: state.currentStep < action.payload.totalSteps - 1
          ? state.currentStep + 1
          : state.currentStep,
      };
    }

    // === Form Data ===
    case 'SET_FORM_FIELD':
      return {
        ...state,
        sampleFormData: { ...state.sampleFormData, [action.payload.field]: action.payload.value },
      };

    case 'SET_FORM_DATA':
      return { ...state, sampleFormData: action.payload };

    case 'MERGE_FORM_DATA':
      return { ...state, sampleFormData: { ...state.sampleFormData, ...action.payload } };

    // === Image Handling ===
    case 'SET_SELECTED_IMAGE':
      return {
        ...state,
        selectedImage: action.payload.file,
        imagePreview: action.payload.preview,
      };

    case 'SET_IMAGE_DRAGGING':
      return { ...state, isImageDragging: action.payload };

    // === Submission ===
    case 'SUBMIT_START':
      return { ...state, isSubmitting: true };

    case 'SUBMIT_SUCCESS':
      return {
        ...state,
        isSubmitting: false,
        submittedRequest: action.payload,
        showSuccessScreen: true,
      };

    case 'SUBMIT_FAIL':
      return { ...state, isSubmitting: false };

    // === Draft ===
    case 'SAVE_DRAFT_START':
      return { ...state, isSavingDraft: true };

    case 'SAVE_DRAFT_SUCCESS':
      return {
        ...state,
        isSavingDraft: false,
        savedDraftId: action.payload.id,
        savedDraftInfo: action.payload,
        showDraftSuccessScreen: true,
      };

    case 'SAVE_DRAFT_FAIL':
      return { ...state, isSavingDraft: false };

    // === Benchmark ===
    case 'SET_INPUT_MODE':
      return { ...state, inputMode: action.payload };

    case 'BENCHMARK_UPLOAD_START':
      return { ...state, isUploadingBenchmark: true, analysisError: null };

    case 'BENCHMARK_UPLOAD_SUCCESS':
      return {
        ...state,
        isUploadingBenchmark: false,
        benchmarkImageUrl: action.payload.url,
        benchmarkImagePreview: action.payload.preview,
        benchmarkStorageKey: action.payload.storageKey,
      };

    case 'BENCHMARK_UPLOAD_FAIL':
      return { ...state, isUploadingBenchmark: false };

    case 'BENCHMARK_ANALYZE_START':
      return { ...state, isAnalyzingBenchmark: true, analysisError: null };

    case 'BENCHMARK_ANALYZE_SUCCESS':
      return {
        ...state,
        isAnalyzingBenchmark: false,
        benchmarkAnalysisResult: action.payload.result,
        sampleFormData: action.payload.mergedFormData,
      };

    case 'BENCHMARK_ANALYZE_FAIL':
      return {
        ...state,
        isAnalyzingBenchmark: false,
        analysisError: action.payload,
      };

    case 'CLEAR_BENCHMARK':
      return {
        ...state,
        benchmarkImagePreview: '',
        benchmarkImageUrl: '',
        benchmarkStorageKey: '',
        benchmarkAnalysisResult: null,
        analysisError: null,
      };

    case 'PROCEED_FROM_ANALYSIS':
      return {
        ...state,
        inputMode: 'manual',
        currentStep: action.payload.step,
        imagePreview: state.benchmarkImagePreview || state.benchmarkImageUrl || state.imagePreview,
        sampleFormData: {
          ...state.sampleFormData,
          reference_packaging_image: state.benchmarkImageUrl || state.sampleFormData.reference_packaging_image,
        },
      };

    // === Email Verification ===
    case 'SEND_VERIFICATION_START':
      return { ...state, isSendingVerification: true };

    case 'SEND_VERIFICATION_SUCCESS':
      return { ...state, isSendingVerification: false, emailVerificationSent: true };

    case 'SEND_VERIFICATION_FAIL':
      return { ...state, isSendingVerification: false };

    case 'SET_VERIFICATION_CODE':
      return { ...state, verificationCode: action.payload };

    case 'VERIFY_EMAIL_START':
      return { ...state, isVerifyingEmail: true };

    case 'VERIFY_EMAIL_SUCCESS':
      return {
        ...state,
        isVerifyingEmail: false,
        emailVerified: true,
        emailVerificationSent: false,
        verificationCode: '',
      };

    case 'VERIFY_EMAIL_FAIL':
      return { ...state, isVerifyingEmail: false };

    // === Password Login ===
    case 'TOGGLE_PASSWORD_LOGIN':
      return { ...state, showPasswordLogin: action.payload };

    case 'SET_LOGIN_PASSWORD':
      return { ...state, loginPassword: action.payload };

    case 'PASSWORD_LOGIN_START':
      return { ...state, isPasswordLoggingIn: true };

    case 'PASSWORD_LOGIN_SUCCESS':
      return {
        ...state,
        isPasswordLoggingIn: false,
        emailVerified: true,
        showPasswordLogin: false,
        loginPassword: '',
      };

    case 'PASSWORD_LOGIN_FAIL':
      return { ...state, isPasswordLoggingIn: false };

    // === Auth State Sync ===
    case 'AUTH_LOGOUT_RESET':
      return {
        ...state,
        emailVerified: false,
        emailVerificationSent: false,
        verificationCode: '',
        isVerifyingEmail: false,
        isSendingVerification: false,
        showPasswordLogin: false,
        loginPassword: '',
        isPasswordLoggingIn: false,
      };

    case 'AUTH_PREFILL':
      return {
        ...state,
        sampleFormData: { ...state.sampleFormData, ...action.payload.formData },
        currentStep: state.currentStep <= 3 ? action.payload.minStep : state.currentStep,
      };

    // === Admin Brand Selection ===
    case 'SET_SELECTED_BRAND_ID':
      return { ...state, selectedBrandId: action.payload };

    // === UI ===
    case 'SET_COPIED_FIELD':
      return { ...state, copiedField: action.payload };

    case 'SET_SHOW_SUCCESS_SCREEN':
      return { ...state, showSuccessScreen: action.payload };

    case 'SET_SHOW_DRAFT_SUCCESS_SCREEN':
      return { ...state, showDraftSuccessScreen: action.payload };

    case 'SET_EMAIL_VERIFIED':
      return { ...state, emailVerified: action.payload };

    default:
      return state;
  }
}
